import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { withTranslation } from 'react-i18next'
import { useQuery } from '@apollo/client'
import dayjs from 'dayjs'
import { Table, Tag, Button, Spin, Alert, Tooltip, Select } from 'antd'

import companyActions from '../../redux/customers/companies/actions'
import CompanyFormContainer from '../Customers/Companies/CompanyFormContainer'
import AddContactModal from '../Customers/dashboard/AddContactModal'
import {
    compactBahtFromSatangs,
    compactBahtFromValue,
    PROJECT_STATUS_LABELS,
    PROJECT_STATUS_COLORS
} from '../Customers/dashboard/format'
import { SEGMENT_COLORS, SEGMENT_LABELS } from '../Customers/CustomerLifetimeValue/format'
import {
    companyTypeTag,
    isQuotationState,
    activityDotColor
} from './detail/format'
import { buildOrderColumns, ORDER_TABLE_PAGINATION } from '../Customers/detail/orderColumns'
import { COMPANY_DETAIL_QUERY } from './detail/queries'
import CompanyDetailWrapper from './detail/companyDetail.style'

const segmentTag = (seg) => {
    const key = seg || 'unsegmented'
    return <Tag color={SEGMENT_COLORS[key]}>{SEGMENT_LABELS[key]}</Tag>
}

// Build the snake_case shape CompanyFormContainer's edit form expects from the
// camelCase GraphQL company.
const toFormValue = (company) => ({
    id: company.id,
    name: company.name,
    tax_id: company.taxId,
    phone_number: company.phoneNumber,
    line_id: company.lineId,
    email: company.email,
    address_detail: company.addressDetail,
    postal_code: company.postalCode,
    province: company.province ? { id: company.province.id } : null,
    district: company.district ? { id: company.district.id } : null
})

const CompanyInfoContainer = ({ companyId }) => {
    const dispatch = useDispatch()
    const editModalOpen = useSelector((state) => state.getIn(['customerCompanies', 'openAddcompanyModal']))

    // Company whose "add contact" modal is open (null = closed).
    const [contactCompany, setContactCompany] = useState(null)

    // Client-side filters for the recent orders/quotations table.
    const emptyOrderFilters = { kind: null, state: null, project: null, contact: null }
    const [orderFilters, setOrderFilters] = useState(emptyOrderFilters)
    const setOrderFilter = (key, value) => setOrderFilters((prev) => ({ ...prev, [key]: value }))

    const { data, loading, error, refetch } = useQuery(COMPANY_DETAIL_QUERY, {
        variables: { id: companyId }
    })

    const company = data && data.company
    const perf = company && company.performance
    const contacts = (company && company.contacts && company.contacts.nodes) || []
    const orders = (company && company.recentOrders && company.recentOrders.nodes) || []
    const projects = (company && company.projects && company.projects.nodes) || []
    const activities = (company && company.recentActivities) || []

    const primaryContact = contacts[0]

    const typeTag = useMemo(() => company && companyTypeTag(company.companyType), [company])

    // Filter dropdown options, derived from the full (unfiltered) orders list so
    // choices stay stable as the user narrows down.
    const stateOptions = useMemo(() => {
        const map = new Map()
        orders.forEach((o) => { if (o.state && !map.has(o.state)) map.set(o.state, o.stateLabel || o.state) })
        return Array.from(map, ([value, label]) => ({ value, label }))
    }, [orders])

    const projectOptions = useMemo(() => {
        const map = new Map()
        orders.forEach((o) => { if (o.companyProject) map.set(o.companyProject.id, o.companyProject.name || '-') })
        return Array.from(map, ([value, label]) => ({ value, label }))
    }, [orders])

    const contactOptions = useMemo(() => {
        const map = new Map()
        orders.forEach((o) => {
            if (o.customer) map.set(o.customer.customerNo, o.customer.fullName || o.customer.customerNo)
        })
        return Array.from(map, ([value, label]) => ({ value, label }))
    }, [orders])

    const filteredOrders = useMemo(() => orders.filter((o) => {
        if (orderFilters.kind === 'quotation' && !isQuotationState(o.state)) return false
        if (orderFilters.kind === 'order' && isQuotationState(o.state)) return false
        if (orderFilters.state && o.state !== orderFilters.state) return false
        if (orderFilters.project && !(o.companyProject && o.companyProject.id === orderFilters.project)) return false
        if (orderFilters.contact && !(o.customer && o.customer.customerNo === orderFilters.contact)) return false
        return true
    }), [orders, orderFilters])

    const ordersFiltered = filteredOrders.length !== orders.length

    const openEdit = () => dispatch(companyActions.setAddCompanyModal(true))

    // ----- cell renderers -----
    const money = (satangs) => <span className="num">{compactBahtFromSatangs(satangs)}</span>

    const contactColumns = [
        {
            title: 'ชื่อ',
            key: 'name',
            render: (c) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <Link to={`/customers/${c.customerNo}`}>{c.fullName || c.customerNo}</Link>
                    {segmentTag(c.performance && c.performance.clvSegment)}
                </div>
            )
        },
        {
            title: 'เบอร์โทรศัพท์',
            dataIndex: 'phoneNumber',
            width: 130,
            render: (v) => v || <span className="cell-secondary">-</span>
        },
        {
            title: 'อีเมล / ไลน์ไอดี',
            key: 'contact',
            width: 220,
            render: (c) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span>{c.email || <span className="cell-secondary">-</span>}</span>
                    <span className="cell-secondary">
                        Line: {c.lineUrl
                            ? <a href={c.lineUrl} target="_blank" rel="noopener noreferrer">{c.lineId || '-'}</a>
                            : (c.lineId || '-')}
                    </span>
                </div>
            )
        },
        {
            title: '',
            key: 'act',
            width: 120,
            render: (c) => (
                <div style={{ display: 'flex', gap: 12, whiteSpace: 'nowrap' }}>
                    {c.phoneNumber ? <a href={`tel:${c.phoneNumber}`}>โทร</a> : null}
                    <Link to={`/customers/${c.customerNo}/edit`}>แก้ไข</Link>
                </div>
            )
        }
    ]

    // Same order-table format as the Customer detail overview (shared builder),
    // plus a ผู้ติดต่อ column since a company's orders span many contacts.
    const orderColumns = buildOrderColumns({
        contactColumn: {
            title: 'ผู้ติดต่อ',
            key: 'contact',
            width: 150,
            render: (o) => (o.customer
                ? <Link to={`/customers/${o.customer.customerNo}`}>{o.customer.fullName || o.customer.customerNo}</Link>
                : <span className="cell-secondary">-</span>)
        }
    })

    // ----- header ----
    const renderHeader = () => {
        const addressParts = [
            company.addressDetail,
            company.district && company.district.name,
            company.province && company.province.name,
            company.postalCode
        ].filter(Boolean)

        const metaBits = []
        if (company.taxId) {
            metaBits.push(
                <React.Fragment key="tax">
                    เลขประจำตัวผู้เสียภาษี: <span className="num">{company.taxId}</span>
                </React.Fragment>
            )
        }
        if (company.createdAt && company.createdAt.raw) {
            metaBits.push(<span key="since">ลูกค้าตั้งแต่ {dayjs(company.createdAt.raw).format('MMM YYYY')}</span>)
        }
        if (company.salesStaff && company.salesStaff.length) {
            metaBits.push(<span key="sales">เซลล์ผู้ดูแล: {company.salesStaff.map((s) => s.fullName).join(', ')}</span>)
        }

        return (
            <div className="card card--padded">
                <div className="header-top">
                    <div className="header-identity">
                        <div className="avatar">{(company.name || '?').trim().charAt(0)}</div>
                        <div className="identity-lines">
                            <div className="company-name-row">
                                <span className="company-name">{company.name}</span>
                                {typeTag && <Tag color={typeTag.color} style={{ margin: 0 }}>{typeTag.label}</Tag>}
                            </div>
                            <div className="company-meta">
                                {metaBits.map((bit, i) => (
                                    <React.Fragment key={i}>
                                        {i > 0 && <span className="sep">·</span>}
                                        {bit}
                                    </React.Fragment>
                                ))}
                            </div>
                            {addressParts.length > 0 && (
                                <div className="company-address">{addressParts.join(' ')}</div>
                            )}
                        </div>
                    </div>
                    <div className="header-actions">
                        <Button onClick={openEdit}>แก้ไข</Button>
                        <Button onClick={() => setContactCompany({ id: company.id, name: company.name })}>
                            + เพิ่มผู้ติดต่อ
                        </Button>
                        {primaryContact ? (
                            <Link to={`/companies/${companyId}/orders/new`}>
                                <Button type="primary">+ เพิ่มใบเสนอราคา</Button>
                            </Link>
                        ) : (
                            <Tooltip title="เพิ่มผู้ติดต่อก่อนจึงจะสร้างใบเสนอราคาได้">
                                <Button type="primary" disabled>+ เพิ่มใบเสนอราคา</Button>
                            </Tooltip>
                        )}
                    </div>
                </div>
                {perf && renderStats()}
            </div>
        )
    }

    const renderStats = () => {
        const tiles = [
            {
                label: 'Lifetime Revenue',
                value: compactBahtFromSatangs(perf.lifetimeRevenueSatangs),
                sub: `จาก ${perf.ordersCount} ออเดอร์`
            },
            {
                label: 'Predicted CLV',
                value: compactBahtFromSatangs(perf.predictedClvSatangs),
                color: '#389e0d'
            },
            {
                label: 'ยอดค้างชำระ',
                value: compactBahtFromSatangs(perf.outstandingSatangs),
                color: '#d48806'
            },
            {
                label: 'ใบเสนอราคาเปิดอยู่',
                value: perf.openQuotationsCount,
                sub: `มูลค่ารวม ${compactBahtFromSatangs(perf.openQuotationsValueSatangs)}`
            },
            {
                label: 'ออเดอร์ล่าสุด',
                value: perf.daysSinceLastOrder != null ? `${perf.daysSinceLastOrder} วัน` : '-',
                sub: perf.lastOrderAt && perf.lastOrderAt.raw ? dayjs(perf.lastOrderAt.raw).format('D MMM YYYY') : null
            }
        ]
        return (
            <div className="stats-grid">
                {tiles.map((t) => (
                    <div className="stat-tile" key={t.label}>
                        <div className="stat-label">{t.label}</div>
                        <div className="stat-value" style={t.color ? { color: t.color } : undefined}>{t.value}</div>
                        {t.sub && <div className="stat-sub">{t.sub}</div>}
                    </div>
                ))}
            </div>
        )
    }

    if (loading) return <Spin style={{ display: 'block', margin: '80px auto' }} />
    if (error) return <Alert type="error" message={error.message} />
    if (!company) return <Alert type="warning" message="ไม่พบข้อมูลบริษัท" />

    return (
        <CompanyDetailWrapper>
            {renderHeader()}

            <div className="body-grid">
                <div className="body-col">
                    <div className="card card--flush">
                        <div className="section-head">
                            <span className="section-title">ผู้ติดต่อ ({contacts.length})</span>
                            <a onClick={() => setContactCompany({ id: company.id, name: company.name })}>+ เพิ่มผู้ติดต่อ</a>
                        </div>
                        {contacts.length > 0 ? (
                            <Table
                                columns={contactColumns}
                                dataSource={contacts}
                                rowKey="id"
                                size="small"
                                pagination={false}
                                scroll={{ x: 560 }}
                            />
                        ) : (
                            <div className="empty">ยังไม่มีผู้ติดต่อ</div>
                        )}
                    </div>

                    <div className="card card--flush">
                        <div className="section-head">
                            <span className="section-title">
                                ใบเสนอราคาและออเดอร์
                                <span className="section-count">
                                    {ordersFiltered ? `${filteredOrders.length}/${orders.length}` : orders.length}
                                </span>
                            </span>
                        </div>
                        {orders.length > 0 && (
                            <div className="filter-bar">
                                <Select
                                    allowClear
                                    placeholder="ประเภท"
                                    value={orderFilters.kind}
                                    onChange={(v) => setOrderFilter('kind', v || null)}
                                    options={[
                                        { value: 'quotation', label: 'ใบเสนอราคา' },
                                        { value: 'order', label: 'ออเดอร์' }
                                    ]}
                                />
                                <Select
                                    allowClear
                                    placeholder="สถานะ"
                                    value={orderFilters.state}
                                    onChange={(v) => setOrderFilter('state', v || null)}
                                    options={stateOptions}
                                />
                                <Select
                                    allowClear
                                    showSearch
                                    optionFilterProp="label"
                                    placeholder="โปรเจกต์"
                                    value={orderFilters.project}
                                    onChange={(v) => setOrderFilter('project', v || null)}
                                    options={projectOptions}
                                />
                                <Select
                                    allowClear
                                    showSearch
                                    optionFilterProp="label"
                                    placeholder="ผู้ติดต่อ"
                                    value={orderFilters.contact}
                                    onChange={(v) => setOrderFilter('contact', v || null)}
                                    options={contactOptions}
                                />
                                {ordersFiltered && (
                                    <a className="filter-reset" onClick={() => setOrderFilters(emptyOrderFilters)}>
                                        ล้างตัวกรอง
                                    </a>
                                )}
                            </div>
                        )}
                        {orders.length === 0 ? (
                            <div className="empty">ยังไม่มีใบเสนอราคาหรือออเดอร์</div>
                        ) : filteredOrders.length > 0 ? (
                            <Table
                                columns={orderColumns}
                                dataSource={filteredOrders}
                                rowKey="id"
                                size="small"
                                pagination={ORDER_TABLE_PAGINATION}
                                scroll={{ x: 900 }}
                            />
                        ) : (
                            <div className="empty">ไม่พบรายการที่ตรงกับตัวกรอง</div>
                        )}
                    </div>
                </div>

                <div className="body-col">
                    <div className="card card--flush">
                        <div className="section-head">
                            <span className="section-title">โปรเจกต์ ({projects.length})</span>
                        </div>
                        {projects.length > 0 ? (
                            projects.map((p) => (
                                <div className="project-row" key={p.id}>
                                    <div className="project-row-top">
                                        <span className="project-name">{p.name || '-'}</span>
                                        {p.status && (
                                            <Tag color={PROJECT_STATUS_COLORS[p.status]} style={{ margin: 0 }}>
                                                {PROJECT_STATUS_LABELS[p.status]}
                                            </Tag>
                                        )}
                                    </div>
                                    <div className="project-row-bottom">
                                        <span>มูลค่า <span className="num">{compactBahtFromValue(p.paidPrice && p.paidPrice.value)}</span></span>
                                        {p.unpaidPrice && p.unpaidPrice.value > 0 && (
                                            <span>ค้าง {compactBahtFromValue(p.unpaidPrice.value)}</span>
                                        )}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="empty">ยังไม่มีโปรเจกต์</div>
                        )}
                    </div>

                    <div className="card card--flush">
                        <div className="section-head">
                            <span className="section-title">กิจกรรมล่าสุด</span>
                        </div>
                        {activities.length > 0 ? (
                            <div className="activity-list">
                                {activities.map((a) => (
                                    <div className="activity-row" key={a.id}>
                                        <div className="activity-dot" style={{ background: activityDotColor(a.kind) }} />
                                        <div className="activity-body">
                                            <span className="activity-text">{a.text}</span>
                                            <span className="activity-when">
                                                {a.occurredAt && a.occurredAt.raw ? dayjs(a.occurredAt.raw).fromNow() : ''}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="empty">ยังไม่มีกิจกรรม</div>
                        )}
                    </div>
                </div>
            </div>

            <CompanyFormContainer
                visible={!!editModalOpen}
                defaultValue={toFormValue(company)}
                title="แก้ไขบริษัท"
                onSaveSuccess={() => refetch()}
            />

            <AddContactModal
                company={contactCompany}
                visible={!!contactCompany}
                onClose={() => setContactCompany(null)}
                onSuccess={() => { refetch(); setContactCompany(null) }}
            />
        </CompanyDetailWrapper>
    )
}

export default withTranslation(['companies'])(CompanyInfoContainer)
